import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth, API } from '../App';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { 
  CheckCircle, 
  Loader2,
  Crown,
  Sparkles,
  ArrowRight,
  XCircle
} from 'lucide-react';

export default function SubscriptionSuccessPage() {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('loading'); // loading, success, failed
  const [subscription, setSubscription] = useState(null);

  const sessionId = searchParams.get('session_id');

  const checkStatus = useCallback(async () => {
    if (!sessionId) {
      setStatus('failed');
      return;
    }

    let attempts = 0;
    const maxAttempts = 10;

    const poll = async () => {
      try {
        const response = await axios.get(`${API}/subscriptions/status/${sessionId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });

        if (response.data.payment_status === 'paid') {
          setStatus('success');
          // Fetch subscription details
          const subResponse = await axios.get(`${API}/subscriptions/my`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setSubscription(subResponse.data);
          toast.success('Welcome to the family!');
          return;
        } else if (response.data.status === 'expired') {
          setStatus('failed');
          return;
        }

        attempts++;
        if (attempts < maxAttempts) {
          setTimeout(poll, 2000);
        } else {
          // Still pending after max attempts
          setStatus('success'); // Assume it worked, backend will catch up
        }
      } catch (error) {
        console.error('Error checking status:', error);
        attempts++;
        if (attempts < maxAttempts) {
          setTimeout(poll, 2000);
        } else {
          setStatus('failed');
        }
      }
    };

    poll();
  }, [sessionId, token]);

  useEffect(() => {
    checkStatus();
  }, [checkStatus]);

  if (status === 'loading') {
    return (
      <div data-testid="subscription-success-loading" className="min-h-[60vh] flex flex-col items-center justify-center">
        <div className="w-20 h-20 bg-primary/20 rounded-full flex items-center justify-center mb-6 animate-pulse">
          <Loader2 className="w-10 h-10 text-primary animate-spin" />
        </div>
        <h2 className="font-heading text-2xl text-white uppercase mb-2">Processing Payment</h2>
        <p className="text-gray-400">Please wait while we confirm your subscription...</p>
      </div>
    );
  }

  if (status === 'failed') {
    return (
      <div data-testid="subscription-failed" className="min-h-[60vh] flex flex-col items-center justify-center">
        <div className="w-20 h-20 bg-red-500/20 rounded-full flex items-center justify-center mb-6">
          <XCircle className="w-10 h-10 text-red-500" />
        </div>
        <h2 className="font-heading text-2xl text-white uppercase mb-2">Payment Issue</h2>
        <p className="text-gray-400 mb-6">There was an issue processing your payment</p>
        <div className="flex gap-3">
          <Button
            variant="outline"
            onClick={() => navigate('/subscription')}
            className="border-border"
          >
            Try Again
          </Button>
          <Button
            onClick={() => navigate('/dashboard')}
            className="bg-primary hover:bg-red-700"
          >
            Go to Dashboard
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="subscription-success-page" className="min-h-[60vh] flex flex-col items-center justify-center">
      {/* Success Animation */}
      <div className="relative mb-8">
        <div className="w-24 h-24 bg-green-500/20 rounded-full flex items-center justify-center">
          <CheckCircle className="w-12 h-12 text-green-500" />
        </div>
        <div className="absolute -top-2 -right-2">
          <Sparkles className="w-8 h-8 text-yellow-500 animate-pulse" />
        </div>
      </div>

      <h1 className="font-heading text-3xl sm:text-4xl text-white uppercase text-center mb-2">
        Welcome to the <span className="text-primary">Family!</span>
      </h1>
      <p className="text-gray-400 text-center max-w-md mb-8">
        Your subscription is now active. Get ready to transform your fitness journey with full access to all features.
      </p>

      {/* Plan Details */}
      {subscription && (
        <div className="card-tactical max-w-sm w-full mb-8">
          <div className="flex items-center gap-4 mb-4">
            <div className="w-12 h-12 bg-yellow-500/20 rounded-full flex items-center justify-center">
              <Crown className="w-6 h-6 text-yellow-500" />
            </div>
            <div>
              <div className="text-white font-semibold capitalize">{subscription.plan} Plan</div>
              <div className="text-sm text-gray-400">${subscription.price}/month</div>
            </div>
          </div>
          <div className="p-3 bg-green-500/10 border border-green-500/30 rounded-sm">
            <div className="flex items-center gap-2 text-green-500">
              <CheckCircle className="w-4 h-4" />
              <span className="text-sm font-medium">Subscription Active</span>
            </div>
          </div>
        </div>
      )}

      {/* What's Unlocked */}
      <div className="max-w-md w-full mb-8">
        <h3 className="text-sm text-gray-400 uppercase tracking-wider text-center mb-4">You now have access to</h3>
        <div className="grid grid-cols-2 gap-3">
          {[
            'AI Workout Builder',
            'AI Meal Plans',
            '1-1 Trainer Chat',
            'Premium Workouts',
            'Priority Support',
            'Progress Analytics'
          ].map((feature, index) => (
            <div key={index} className="flex items-center gap-2 p-3 bg-secondary/50 rounded-sm">
              <CheckCircle className="w-4 h-4 text-primary flex-shrink-0" />
              <span className="text-sm text-gray-300">{feature}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex flex-col sm:flex-row gap-3 w-full max-w-sm">
        <Button
          data-testid="go-to-dashboard-btn"
          onClick={() => navigate('/dashboard')}
          className="flex-1 bg-primary hover:bg-red-700 text-white uppercase font-semibold"
        >
          Go to Dashboard
          <ArrowRight className="w-4 h-4 ml-2" />
        </Button>
        <Button
          data-testid="generate-workout-btn"
          onClick={() => navigate('/workouts')}
          variant="outline"
          className="flex-1 border-border hover:border-primary"
        >
          <Sparkles className="w-4 h-4 mr-2" />
          Generate Workout
        </Button>
      </div>
    </div>
  );
}
